/**
 * WebSocket hook for Family App video calls
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { CallState, WebSocketMessage, CallAcceptedPayload, CallRejectedPayload, CallEndedPayload, WebRTCSignalPayload } from './callTypes.ts';
import { startCall as apiStartCall, endCall as apiEndCall } from './callsApi.ts';

const WS_BASE = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://') || 'wss://api.hayatfamily.app';

const RECONNECT_DELAYS = [1000, 2000, 5000, 10000]; // Backoff delays in ms

/**
 * WebSocket hook for Family App video calls
 * @param userId - The authenticated family member's user ID (required for WebSocket connection)
 */
export function useCallWebSocket(userId?: string) {
  const [wsReady, setWsReady] = useState(false);
  const [currentCall, setCurrentCall] = useState<CallState | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttempt = useRef(0);
  const reconnectTimer = useRef<NodeJS.Timeout | null>(null);
  const webrtcSignalCallbackRef = useRef<((signal: any) => void) | null>(null);
  
  /**
   * Send WebRTC signal to device
   */
  const sendSignal = useCallback((callId: string, signal: any) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.warn('[Call WS] Cannot send signal, WebSocket not ready');
      return;
    }
    
    const message = {
      type: 'webrtc.signal',
      payload: {
        call_id: callId,
        signal,
      },
    };
    
    console.log('[Call WS] Sending signal:', message);
    wsRef.current.send(JSON.stringify(message));
  }, []);
  
  /**
   * Handle incoming WebSocket messages
   */
  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      const message: WebSocketMessage = JSON.parse(event.data);
      console.log('[Call WS] Received:', message);
      
      switch (message.type) {
        case 'call.accepted': {
          const payload = message.payload as CallAcceptedPayload;
          setCurrentCall((prev) => 
            prev?.callId === payload.call_id
              ? { ...prev, status: 'in_call' }
              : prev
          );
          break;
        }
        
        case 'call.rejected': {
          const payload = message.payload as CallRejectedPayload;
          setCurrentCall((prev) =>
            prev?.callId === payload.call_id
              ? { ...prev, status: 'rejected', reason: payload.reason }
              : prev
          );
          // Auto-clear after 3 seconds
          setTimeout(() => {
            setCurrentCall((prev) =>
              prev?.status === 'rejected' ? null : prev
            );
          }, 3000);
          break;
        }
        
        case 'call.ended': {
          const payload = message.payload as CallEndedPayload;
          setCurrentCall((prev) =>
            prev?.callId === payload.call_id
              ? { ...prev, status: 'ended', reason: payload.reason }
              : prev
          );
          // Auto-clear after 3 seconds
          setTimeout(() => {
            setCurrentCall((prev) =>
              prev?.status === 'ended' ? null : prev
            );
          }, 3000);
          break;
        }
        
        case 'webrtc.signal': {
          const payload = message.payload as WebRTCSignalPayload;
          console.log('[Call WS] WebRTC signal received:', payload.signal.type);
          
          // Trigger callback if registered
          if (webrtcSignalCallbackRef.current) {
            webrtcSignalCallbackRef.current(payload.signal);
          }
          break;
        }
        
        case 'emergency.triggered': {
          const payload = message.payload as any;
          console.log('[Family][WS] Emergency triggered:', payload);
          
          // Show emergency notification
          if (typeof window !== 'undefined') {
            // Emergency notification will be handled by importing component
            // Trigger a custom event that can be caught by the UI
            const event = new CustomEvent('emergency-alert', {
              detail: {
                senior_name: payload.senior_name,
                senior_id: payload.senior_id,
                timestamp: payload.timestamp
              }
            });
            window.dispatchEvent(event);
            
            // Browser notification if supported
            if ('Notification' in window && Notification.permission === 'granted') {
              new Notification('🚨 NOTFALL!', {
                body: `${payload.senior_name || 'Senior'} hat einen Notfall ausgelöst!`,
                tag: 'emergency',
                requireInteraction: true
              });
            }
          }
          break;
        }
        
        default:
          console.log('[Call WS] Unknown message type:', message.type);
      }
    } catch (err) {
      console.error('[Call WS] Error handling message:', err);
    }
  }, []);
  
  /**
   * Connect to WebSocket
   */
  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      console.log('[Call WS] Already connected');
      return;
    }
    
    // Require userId for WebSocket connection
    if (!userId) {
      console.warn('[Call WS] Cannot connect: userId is required');
      setError('Benutzer-ID fehlt für Anruf-Verbindung');
      return;
    }
    
    const WS_URL = `${WS_BASE}/api/ws/call/family/${userId}`;
    console.log('[Call WS] Connecting to:', WS_URL);
    
    try {
      const ws = new WebSocket(WS_URL);
      
      ws.onopen = () => {
        console.log('[Call WS] Connected');
        setWsReady(true);
        setError(null);
        reconnectAttempt.current = 0;
      };
      
      ws.onmessage = handleMessage;
      
      ws.onerror = (err) => {
        console.error('[Call WS] Error:', err);
        setError('WebSocket-Verbindung fehlgeschlagen');
      };
      
      ws.onclose = (event) => {
        console.log('[Call WS] Disconnected:', event.code, event.reason);
        setWsReady(false);
        
        // Attempt reconnect with backoff
        const delay = RECONNECT_DELAYS[Math.min(reconnectAttempt.current, RECONNECT_DELAYS.length - 1)];
        console.log(`[Call WS] Reconnecting in ${delay}ms...`);
        
        reconnectTimer.current = setTimeout(() => {
          reconnectAttempt.current++;
          connect();
        }, delay);
      };
      
      wsRef.current = ws;
    } catch (err) {
      console.error('[Call WS] Failed to create WebSocket:', err);
      setError('Anrufe nicht verfügbar');
    }
  }, [handleMessage]);
  
  /**
   * Disconnect WebSocket
   */
  const disconnect = useCallback(() => {
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }
    
    if (wsRef.current) {
      console.log('[Call WS] Disconnecting');
      wsRef.current.close();
      wsRef.current = null;
    }
    
    setWsReady(false);
  }, []);
  
  /**
   * Start a call
   */
  const startCall = useCallback(async (deviceId: string, seniorId: string) => {
    try {
      console.log('[Call WS] Starting call via API:', { deviceId, seniorId });
      
      const response = await apiStartCall(deviceId, seniorId);
      
      setCurrentCall({
        callId: response.call_id,
        deviceId: response.device_id,
        seniorId: response.senior_id,
        status: 'ringing',
      });
      
      return response;
    } catch (err) {
      console.error('[Call WS] Failed to start call:', err);
      throw err;
    }
  }, []);
  
  /**
   * End current call
   */
  const endCall = useCallback(async (callId: string, reason: string = 'ended') => {
    try {
      console.log('[Call WS] Ending call via API:', { callId, reason });
      
      await apiEndCall(callId, reason);
      
      setCurrentCall((prev) =>
        prev?.callId === callId
          ? { ...prev, status: 'ended', reason }
          : prev
      );
      
      // Clear after delay
      setTimeout(() => setCurrentCall(null), 2000);
    } catch (err) {
      console.error('[Call WS] Failed to end call:', err);
      throw err;
    }
  }, []);
  
  /**
   * Clear current call state
   */
  const clearCall = useCallback(() => {
    setCurrentCall(null);
  }, []);
  
  /**
   * Register callback for WebRTC signals
   */
  const onWebRTCSignal = useCallback((callback: (signal: any) => void) => {
    webrtcSignalCallbackRef.current = callback;
  }, []);
  
  // Auto-connect on mount (only when userId is available)
  useEffect(() => {
    if (!userId) {
      console.warn('[Call WS] Skipping auto-connect: userId not provided');
      return undefined;
    }
    
    connect();
    
    return () => {
      disconnect();
    };
  }, [userId, connect, disconnect]);
  
  return {
    wsReady,
    currentCall,
    error,
    startCall,
    endCall,
    clearCall,
    sendSignal,
    onWebRTCSignal,
  };
}
